import React, { useEffect, useMemo, useRef, useState } from "react";
import Slider from "react-slick";
import { getAboutHeroes, getAboutValues, getAboutTeamMembers } from "../services/aboutServices";
import PageHeader from "./common/PageHeader";
import SectionHeader from "./common/SectionHeader";
import CTASection from "./common/CTASection";
import sharedStyles from "./common/shared-styles";

const AboutUs = () => {
  const valuesSliderRef = useRef(null);
  const teamSliderRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [hero, setHero] = useState(null);
  const [values, setValues] = useState([]);
  const [team, setTeam] = useState([]);

  // Slider settings for Values section
  const valuesSettings = useMemo(
    () => ({
      dots: false,
      arrows: false,
      infinite: true,
      speed: 1000,
      slidesToShow: 4,
      slidesToScroll: 1,
      initialSlide: 0,
      responsive: [
        { breakpoint: 1399, settings: { slidesToShow: 3 } },
        { breakpoint: 992, settings: { slidesToShow: 2 } },
        { breakpoint: 768, settings: { slidesToShow: 1 } },
      ],
    }),
    []
  );

  // Slider settings for Team section
  const teamSettings = useMemo(
    () => ({
      dots: false,
      arrows: false,
      infinite: true,
      speed: 1000,
      slidesToShow: 4,
      slidesToScroll: 1,
      initialSlide: 0,
      responsive: [
        { breakpoint: 1399, settings: { slidesToShow: 3 } },
        { breakpoint: 992, settings: { slidesToShow: 2 } },
        { breakpoint: 768, settings: { slidesToShow: 1 } },
      ],
    }),
    []
  );

  // Statistics data (static)
  const stats = [
    { count: "10+", label: "Years Experience" },
    { count: "50K+", label: "Happy Customers" },
    { count: "500+", label: "Products" },
    { count: "25+", label: "Team Members" },
  ];

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [heroes, vals, members] = await Promise.all([
          getAboutHeroes(),
          getAboutValues(),
          getAboutTeamMembers(),
        ]);

        // HERO: prefer active hero, else first
        const activeHero = (heroes || []).find((h) => Number(h.is_active) === 1) || heroes?.[0] || null;
        setHero(activeHero);

        // Values + Team: active only + sort_order
        const v = (vals || [])
          .filter((x) => Number(x.is_active) === 1)
          .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

        const t = (members || [])
          .filter((x) => Number(x.is_active) === 1)
          .sort((a, b) => Number(a.sort_order || 0) - Number(b.sort_order || 0));

        setValues(v);
        setTeam(t);
      } catch (e) {
        console.error("About page load error:", e);
        setHero(null);
        setValues([]);
        setTeam([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  // Fallback hero image if no backend image
  const heroImage = hero?.image_url ;

  return (
    <section className="about-us py-80">
      <style>
        {sharedStyles}
        {`
          /* Additional About-specific styles not in shared */
          .value-card {
            text-center: true;
            padding: 32px;
            border: 1px solid #e5e7eb;
            border-radius: 12px;
            height: 100%;
            transition: all 0.3s ease;
          }

          .value-card:hover {
            border-color: var(--main-600, #2c3e50);
          }
        `}
      </style>

      <div className="container container-lg">
        {/* About Hero Section */}
        <div className="row gy-4 align-items-center mb-80">
          <div className="col-lg-6">
            <div className="about-thumb">
              <img src={heroImage} alt="About Us" className="w-100 rounded-3" />
            </div>
          </div>

          <div className="col-lg-6">
            <div className="about-content">
              <span className="text-main-600 text-uppercase fw-semibold mb-16">
                Who We Are
              </span>

              <h2 className="mb-24">
                {hero?.title || "Building Trust Through Quality & Service"}
              </h2>

              <p className="text-neutral-600 mb-24">
                {hero?.subtitle ||
                  "Founded in 2014, we started with a simple mission: to provide customers with high-quality products at affordable prices, backed by exceptional customer service."}
              </p>

              <blockquote className="text-neutral-600 mb-32">
                {hero?.details_text ||
                  "Today, we're proud to offer an extensive selection of products across multiple categories, all carefully curated to meet the diverse needs of our growing community."}
              </blockquote>

              <div className="flex-align gap-16">
                <a href="/shop" className="btn btn-main rounded-pill">
                  Shop Now
                </a>
                <a href="/contact" className="btn btn-outline-main rounded-pill">
                  Contact Us
                </a>
              </div>

              {loading && (
                <div className="mt-16 text-muted small">
                  Loading about content...
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Statistics Section */}
        <div className="row gy-4 mb-80">
          {stats.map((stat, index) => (
            <div className="col-lg-3 col-sm-6" key={index}>
              <div className="text-center p-24 bg-color-one rounded-3 h-100">
                <h1 className="text-main-600 mb-8">{stat.count}</h1>
                <p className="text-neutral-600 fw-medium mb-0">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Mission & Vision Section */}
        <div className="row gy-4 mb-80">
          <div className="col-lg-6">
            <div className="mission-vision-card bg-color-one p-40 rounded-3 h-100">
              <div className="mb-24">
                <span className="mission-vision-icon w-64 h-64 flex-center bg-main-600 text-white rounded-circle text-2xl mb-16 d-inline-flex">
                  🎯
                </span>
              </div>
              <h3 className="mb-16">Our Mission</h3>
              <p className="text-neutral-600 mb-0">
                To empower our customers by providing access to quality products,
                exceptional service, and a seamless shopping experience.
              </p>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="mission-vision-card bg-color-one p-40 rounded-3 h-100">
              <div className="mb-24">
                <span className="mission-vision-icon w-64 h-64 flex-center bg-main-600 text-white rounded-circle text-2xl mb-16 d-inline-flex">
                  👁️
                </span>
              </div>
              <h3 className="mb-16">Our Vision</h3>
              <p className="text-neutral-600 mb-0">
                To become the most trusted and customer-centric e-commerce
                platform, known for our integrity, innovation, and commitment to sustainability.
              </p>
            </div>
          </div>
        </div>

        {/* Core Values Section - Using SectionHeader component */}
        <div className="mb-80">
          <SectionHeader
            badge="Our Values"
            title="What Drives Us Forward"
            description="Our core values are the foundation of everything we do."
          />

          {values.length === 0 ? (
            <div className="text-center text-muted py-32">
              No values added yet.
            </div>
          ) : (
            <div className="position-relative arrow-center">
              {/* External Arrows for Values */}
              <div className="flex-align">
                <button
                  type="button"
                  onClick={() => valuesSliderRef.current?.slickPrev()}
                  className="slick-prev slick-arrow flex-center rounded-circle bg-white text-xl hover-bg-main-600 hover-text-white transition-1"
                  aria-label="Previous"
                >
                  <i className="ph ph-caret-left" />
                </button>

                <button
                  type="button"
                  onClick={() => valuesSliderRef.current?.slickNext()}
                  className="slick-next slick-arrow flex-center rounded-circle bg-white text-xl hover-bg-main-600 hover-text-white transition-1"
                  aria-label="Next"
                >
                  <i className="ph ph-caret-right" />
                </button>
              </div>

              <Slider ref={valuesSliderRef} {...valuesSettings}>
                {values.map((value) => (
                  <div key={value.id} className="px-2">
                    <div className="value-card text-center p-32 border border-neutral-100 rounded-3 h-100 hover-border-main-600 transition-2">
                      {/* Icon with animation wrapper */}
                      <div className="value-icon-wrapper">
                        {value.icon_url ? (
                          <img
                            src={value.icon_url}
                            alt={value.title}
                            style={{ width: 60, height: 60, objectFit: "cover" }}
                            className="mb-24 d-block mx-auto"
                          />
                        ) : (
                          <span className="text-5xl mb-24 d-block">—</span>
                        )}
                      </div>

                      <h5 className="mb-16">{value.title}</h5>
                      <p className="text-neutral-600 mb-0">{value.subtitle || ""}</p>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          )}
        </div>

        {/* Team Section - Using SectionHeader component */}
        <div className="mb-80">
          <SectionHeader
            badge="Our Team"
            title="Meet The People Behind Our Success"
            description="Our dedicated team works tirelessly to bring you the best products and service."
          />

          {team.length === 0 ? (
            <div className="text-center text-muted py-32">
              No team members added yet.
            </div>
          ) : (
            <div className="position-relative arrow-center">
              {/* External Arrows for Team */}
              <div className="flex-align">
                <button
                  type="button"
                  onClick={() => teamSliderRef.current?.slickPrev()}
                  className="slick-prev slick-arrow flex-center rounded-circle bg-white text-xl hover-bg-main-600 hover-text-white transition-1"
                  aria-label="Previous"
                >
                  <i className="ph ph-caret-left" />
                </button>

                <button
                  type="button"
                  onClick={() => teamSliderRef.current?.slickNext()}
                  className="slick-next slick-arrow flex-center rounded-circle bg-white text-xl hover-bg-main-600 hover-text-white transition-1"
                  aria-label="Next"
                >
                  <i className="ph ph-caret-right" />
                </button>
              </div>

              <Slider ref={teamSliderRef} {...teamSettings}>
                {team.map((member) => (
                  <div key={member.id} className="px-2">
                    <div className="text-center">
                      <div
                        className="mb-24 mx-auto position-relative"
                        style={{ width: "200px", height: "200px", padding: "8px" }}
                      >
                        <img
                          src={member.image_url || "/assets/images/user.png"}
                          alt={member.name}
                          className="team-member-image w-100 h-100"
                          style={{ objectFit: "cover" }}
                        />
                      </div>
                      <h5 className="mb-8">{member.name}</h5>
                      <span className="text-main-600 fw-medium d-block mb-12">
                        {member.title || ""}
                      </span>
                      <p className="text-neutral-600 text-sm mb-0">
                        {member.subtitle || ""}
                      </p>
                    </div>
                  </div>
                ))}
              </Slider>
            </div>
          )}
        </div>

        {/* CTA Section - Using CTASection component */}
        <CTASection
          title="Ready to Start Shopping?"
          description="Join thousands of satisfied customers who trust us for their shopping needs."
          primaryButton={{
            text: "Explore Products",
            href: "/shop"
          }}
          secondaryButton={{
            text: "Get In Touch",
            href: "/contact"
          }}
        />
      </div>
    </section>
  );
};

export default AboutUs;